import SoftphoneAPI from '@/api/softphone.api'
import {
  SOFTPHONE_EVENTS,
  createSoftphoneTransport,
  exposeSoftphoneDevHook,
  removeSoftphoneDevHook
} from '@/services/softphone'
import { createSoftphoneOwnership } from '@/services/softphone/softphoneOwnership'
import { armRingtone, startRinging, stopRinging } from '@/services/softphone/ringtone'
import { goOfflineWithKeepalive } from '@/services/softphone/goOfflineBeacon'
import { createSoftphoneChannel, SOFTPHONE_COMMANDS } from '@/services/softphone/softphoneChannel'
import { getReconnectDelayInMs, getReconnectPolicy } from '@/services/softphone/reconnectPolicy'

/**
 * `otherTab` is not part of the call lifecycle: it is where this tab lands when another tab takes
 * the softphone over, so two tabs can never answer for the same agent.
 */
export const SOFTPHONE_STATUS = Object.freeze({
  OFFLINE: 'offline',
  CONNECTING: 'connecting',
  READY: 'ready',
  RINGING: 'ringing',
  ACTIVE: 'active',
  WRAP_UP: 'wrapUp',
  /**
   * The registration went away without the agent asking, and the widget is getting it back. Its own
   * status rather than a second meaning for `CONNECTING`: connecting is what the agent started by
   * pressing Go Live and what they are waiting for, this is unattended and there is nothing for
   * them to do about it - and the widget has to say which of the two it is, because the campaign is
   * paused here and will resume by itself.
   */
  RECONNECTING: 'reconnecting',
  OTHER_TAB: 'otherTab',
  ERROR: 'error'
})

/**
 * Why a reconnect is running. Only the first wait differs: a socket that dropped is given the
 * backoff's first delay, which is also the window in which the SDK's own recovery can beat us to
 * it, while a token being replaced before it expires is nobody's failure and starts at once.
 */
export const RECONNECT_REASONS = Object.freeze({
  SOCKET_CLOSED: 'socketClosed',
  TOKEN_RENEWAL: 'tokenRenewal'
})

/**
 * Which tab this store is. The softphone lives in the header of every dashboard tab, but exactly
 * one of them OWNS the transport and may touch a call; the rest are MIRRORS that render the owner's
 * broadcasts and ask the owner to act. A store that has not been told which it is does neither.
 *
 * The role is not fixed for the life of a tab: a mirror inherits ownership when the owner's tab
 * goes away, and an owner becomes a mirror when the agent takes the softphone over somewhere else.
 * See `services/softphone/softphoneOwnership.js` for how the two are kept to exactly one.
 */
export const SOFTPHONE_ROLES = Object.freeze({
  OWNER: 'owner',
  MIRROR: 'mirror'
})

/**
 * Post-call wrap-up. An answered call that runs past WRAP_UP_ELIGIBLE_CALL_DURATION_IN_MS earns the
 * agent a breather: the widget pauses the live campaign server-side while the call is still up, and
 * once it ends holds WRAP_UP_DURATION_IN_MS before resuming the campaign. Shorter calls (and declines)
 * go straight back to Ready - the campaign was never paused, so there is nothing to wait for.
 *
 * The pause happens DURING the call rather than at hang-up so that a call already being routed as
 * the agent hangs up finds the campaign paused, not a widget that is about to stop answering.
 */
export const WRAP_UP_ELIGIBLE_CALL_DURATION_IN_MS = 15000
export const WRAP_UP_DURATION_IN_MS = 60000

const STATUS_LABELS = {
  [SOFTPHONE_STATUS.OFFLINE]: 'Offline',
  [SOFTPHONE_STATUS.CONNECTING]: 'Connecting',
  [SOFTPHONE_STATUS.READY]: 'Ready',
  [SOFTPHONE_STATUS.RINGING]: 'Incoming',
  [SOFTPHONE_STATUS.ACTIVE]: 'On call',
  [SOFTPHONE_STATUS.WRAP_UP]: 'Wrap-up',
  [SOFTPHONE_STATUS.RECONNECTING]: 'Reconnecting',
  [SOFTPHONE_STATUS.OTHER_TAB]: 'Other tab',
  [SOFTPHONE_STATUS.ERROR]: 'Error'
}

/**
 * The badge colour each status wears, for the surfaces that render the status as a Bootstrap badge
 * rather than with the widget's own CSS. As close to the widget's palette as the variants get:
 * green is taking calls, yellow is live but not this second, grey is not taking calls at all.
 *
 * `light` for a call in progress, matching the widget's own On call badge, which is the one it
 * deliberately leaves the colour of the panel behind it - the agent is already on the call and does
 * not need to be told about it in colour.
 */
const STATUS_BADGE_VARIANTS = {
  [SOFTPHONE_STATUS.OFFLINE]: 'secondary',
  [SOFTPHONE_STATUS.CONNECTING]: 'warning',
  [SOFTPHONE_STATUS.READY]: 'success',
  [SOFTPHONE_STATUS.RINGING]: 'info',
  [SOFTPHONE_STATUS.ACTIVE]: 'light',
  [SOFTPHONE_STATUS.WRAP_UP]: 'warning',
  // Yellow with the wrap-up and connecting: live but not taking calls this second, and on its way
  // back. Deliberately not `danger` - nothing has failed yet, and a red badge on every Wi-Fi hop
  // teaches the agent to ignore red.
  [SOFTPHONE_STATUS.RECONNECTING]: 'warning',
  [SOFTPHONE_STATUS.OTHER_TAB]: 'secondary',
  [SOFTPHONE_STATUS.ERROR]: 'danger'
}

/**
 * The widget's own wording, for anything outside the widget that has to say the same thing. An
 * unknown status reads Offline: the widget is not taking calls, which is the truth for every state
 * this store does not have a word for.
 */
export function getSoftphoneStatusLabel(status) {
  return STATUS_LABELS[status] || STATUS_LABELS[SOFTPHONE_STATUS.OFFLINE]
}

export function getSoftphoneStatusBadgeVariant(status) {
  return STATUS_BADGE_VARIANTS[status] || STATUS_BADGE_VARIANTS[SOFTPHONE_STATUS.OFFLINE]
}

// Transport, ownership lock and timers are deliberately module scoped: they are neither
// serialisable nor reactive, and keeping them out of state means strict mode never sees them mutate.
let transport = null
let ownership = null
let wrapUpTimerId = null
let wrapUpEligibilityTimerId = null

/**
 * A wrap-up pause the call earned but the network refused to take.
 *
 * The eligibility timer fires once per call and is cleared when it does, so a pause that failed
 * used to be the end of it - and the failure that matters is not the server saying no, it is the
 * agent's connection being down, which is exactly when the reconnect is about to put it back. A
 * lid closed mid-call therefore cost the agent the break the call had already earned, silently,
 * with only a red message left behind to say so.
 */
let isWrapUpPauseOwed = false

/**
 * The call whose ending has already been handled, so the SDK's second report of it is recognised
 * as a repeat - see `handleCallEnded`. Call ids are unique, so this is never cleared between
 * calls; it is only reset with the module.
 */
let endedCallId = null

/**
 * The reconnect in flight, or null. One object per run rather than a handful of flags, because
 * every cancellation point - going offline, losing the tab lock, unloading, the SDK recovering by
 * itself - has to stop exactly the run it was looking at and not a later one that started while it
 * was awaiting something.
 *
 * `{ attempt, isCancelled, hasFinished, resumeOnSuccess, restoreStatus, timerId, resolveWait }`.
 * The loop checks `isCancelled` after every await; `resolveWait` is how a cancellation wakes a run
 * that is sitting in a backoff instead of leaving the timer to expire.
 */
let reconnectRun = null

/**
 * A reconnect that is owed but must not start yet, because a call is on. Rebuilding the client
 * under a live call would hang up on the caller to fix the socket, so the work is remembered and
 * `handleCallEnded` starts the loop once the call is over.
 *
 * It holds the REASON rather than a flag, because the two reasons are owed for opposite conditions
 * and only one of them is a broken registration. A `TOKEN_RENEWAL` deferral is sitting on a healthy
 * socket, so a drop arriving during that call still has to be acted on - `handleUnregistered`
 * upgrades it, and a flag would have swallowed exactly the drop the campaign pause exists for.
 */
let deferredReconnectReason = null

/**
 * The bound on a deferral that is waiting for a call to end over a socket that has already gone.
 *
 * `handleCallEnded` is driven by the SDK's `telnyx.notification`, which travels over that socket,
 * and `keepConnectionAliveOnSocketClose` stops the SDK tearing the call down by itself - so a far
 * end that hangs up during the outage cannot tell this tab, and the deferral would wait for an
 * event that is never coming. Only armed for a DROP; a token renewal is deferred behind a call
 * whose signalling still works.
 */
let deferredReconnectTimerId = null

let tokenRenewalTimerId = null

// Set for the duration of `goOffline`. Both transports emit UNREGISTERED from their own
// `disconnect()`, and that event now takes the agent offline itself - without this flag the
// teardown would re-enter itself and pause the campaign a second time.
let isGoingOffline = false
let channel = null

// The teardown in flight, if any. Logging out and unloading can both ask for it, and a teardown
// whose pause request fails can trigger a logout of its own - so a second request joins the first
// rather than starting over with a transport the first is still disconnecting.
let teardownPromise = null

// The channel's handlers are append-only, and a tab changes role at runtime, so they are registered
// once per channel and each one checks the role it is for. Registering them per role transition
// would stack a duplicate handler on every handover.
let areChannelHandlersRegistered = false

export function getSoftphoneChannel() {
  return channel
}

function clearWrapUpTimer() {
  if (wrapUpTimerId) {
    clearTimeout(wrapUpTimerId)
    wrapUpTimerId = null
  }
}

/**
 * Forgets the wrap-up the current call is earning, whether it is still being earned (the timer) or
 * has been earned and is waiting on a server that would not take it (the owed pause).
 *
 * Both belong to ONE call and neither may follow the agent into the next, so they are dropped
 * together - which is what puts the owed pause on every path that ends a call or a shift without
 * each of them having to remember it.
 */
function clearWrapUpEligibilityTimer() {
  isWrapUpPauseOwed = false

  if (wrapUpEligibilityTimerId) {
    clearTimeout(wrapUpEligibilityTimerId)
    wrapUpEligibilityTimerId = null
  }
}

function _clearTokenRenewalTimer() {
  if (tokenRenewalTimerId) {
    clearTimeout(tokenRenewalTimerId)
    tokenRenewalTimerId = null
  }
}

/**
 * A backoff wait that can be woken. The loop awaits this; a cancellation resolves it immediately
 * so the loop reaches its next `isCancelled` check now rather than when the timer would have run -
 * which is what makes Go Offline during a 15 second backoff feel like a click rather than a wish.
 */
function _waitForReconnectDelay(run, delayInMs) {
  return new Promise(resolve => {
    const settle = () => {
      run.timerId = null
      run.resolveWait = null

      resolve()
    }

    run.resolveWait = settle
    run.timerId = setTimeout(settle, delayInMs)
  })
}

function _clearDeferredReconnect() {
  deferredReconnectReason = null

  if (deferredReconnectTimerId) {
    clearTimeout(deferredReconnectTimerId)
    deferredReconnectTimerId = null
  }
}

/**
 * Remembers a reconnect that is owed until the call ends, and - for a drop - bounds the wait.
 *
 * The timeout dispatches `handleCallEnded` rather than starting the run directly, because the call
 * this tab believes it is on is over in every sense that matters: its signalling went with the
 * socket, so it can be neither ended by the far end nor transferred, and leaving it on screen would
 * hold the widget in a state with no exit but Hang up. Settling it through the ordinary path is
 * what then starts the run, with the status the call left behind.
 */
function _deferReconnect(reason, dispatch) {
  _clearDeferredReconnect()

  deferredReconnectReason = reason

  if (reason !== RECONNECT_REASONS.SOCKET_CLOSED) {
    return
  }

  deferredReconnectTimerId = setTimeout(() => {
    deferredReconnectTimerId = null

    dispatch('handleCallEnded')
  }, getReconnectPolicy().deferredReconnectTimeoutInMs)
}

/**
 * Stops the reconnect, whether it is waiting, attempting, or only owed. Safe to call when there is
 * none: every teardown path calls it unconditionally rather than asking first.
 */
function _cancelReconnect() {
  _clearDeferredReconnect()

  const run = reconnectRun

  if (!run) {
    return
  }

  reconnectRun = null
  run.isCancelled = true

  if (run.timerId) {
    clearTimeout(run.timerId)
    run.timerId = null
  }

  if (run.resolveWait) {
    run.resolveWait()
  }
}

/**
 * The token endpoint refusing the agent rather than failing to answer. There is nothing to retry:
 * the session behind the request is gone, and every further attempt would be refused the same way
 * while the agent watches a widget that looks busy.
 */
function _isSessionGoneError(error) {
  const status = error?.response?.status

  return status === 401 || status === 403
}

/**
 * `RECONNECTING` counts as online: the agent's SHIFT is still on - they have a live campaign and
 * they have not asked to stop - and it is what keeps Go Offline alive while the widget is getting
 * the registration back. An agent who has given up waiting must be able to end the shift, and a
 * dead Go Offline button would leave them nothing to press but the backoff.
 */
const ONLINE_STATUSES = Object.freeze([
  SOFTPHONE_STATUS.CONNECTING,
  SOFTPHONE_STATUS.READY,
  SOFTPHONE_STATUS.RINGING,
  SOFTPHONE_STATUS.ACTIVE,
  SOFTPHONE_STATUS.WRAP_UP,
  SOFTPHONE_STATUS.RECONNECTING
])

const IN_CALL_STATUSES = Object.freeze([
  SOFTPHONE_STATUS.RINGING,
  SOFTPHONE_STATUS.ACTIVE
])

/**
 * The states a lost registration is worth reconnecting from: a shift that was REGISTERED and is
 * now not. `CONNECTING` is deliberately absent - a drop there belongs to the Go Live the agent is
 * standing in front of, `goOnline` reports it as a failed go-live, and a reconnect racing that
 * would fight the agent's own next click.
 */
const RECONNECTABLE_STATUSES = Object.freeze([
  SOFTPHONE_STATUS.READY,
  SOFTPHONE_STATUS.RINGING,
  SOFTPHONE_STATUS.ACTIVE,
  SOFTPHONE_STATUS.WRAP_UP
])

function _ensureChannel() {
  if (!channel) {
    channel = createSoftphoneChannel()
  }

  return channel
}

/**
 * Gives up everything this tab holds at the tab level: the ownership lock (or its place in the
 * queue for it) and the channel. Owner and mirror alike, and nothing here touches a call or the
 * server - see `teardownSoftphone` for what comes before it in the owner.
 */
function _disposeTabResources() {
  if (ownership) {
    ownership.destroy()
    ownership = null
  }

  if (channel) {
    channel.close()
    channel = null
    areChannelHandlersRegistered = false
  }
}

/**
 * Drops the transport without waiting for it. The unload path cannot wait: the Telnyx disconnect
 * settles on a socket event, and a page in `pagehide` is gone before that arrives. Listeners go
 * first so the disconnect's own UNREGISTERED cannot land in a store that has already been reset.
 */
function _dropTransportWithoutWaiting() {
  if (!transport) {
    return
  }

  const currentTransport = transport

  transport = null

  currentTransport.removeAllListeners()
  removeSoftphoneDevHook()

  try {
    Promise.resolve(currentTransport.disconnect()).catch(error => {
      console.error('Failed to disconnect softphone transport', error)
    })
  } catch (error) {
    console.error('Failed to disconnect softphone transport', error)
  }
}

/**
 * Points the current transport's events at the store. Shared by the go-live that builds the first
 * transport and by every reconnect attempt that builds a replacement, so a reconnected softphone
 * cannot end up half-wired - the failure that leaves a widget registered and deaf.
 *
 * `UNREGISTERED` is the one event whose payload is read here: `requested` tells a teardown from a
 * drop, and only the drop is reconnected from.
 */
function _wireTransportEvents({ dispatch }) {
  transport.on(SOFTPHONE_EVENTS.REGISTERED, () => dispatch('handleRegistered'))
  transport.on(SOFTPHONE_EVENTS.UNREGISTERED, payload => dispatch('handleUnregistered', payload))
  transport.on(SOFTPHONE_EVENTS.INCOMING, payload => dispatch('handleIncomingCall', payload))
  transport.on(SOFTPHONE_EVENTS.ANSWERED, payload => dispatch('handleCallAnswered', payload))
  transport.on(SOFTPHONE_EVENTS.ENDED, payload => dispatch('handleCallEnded', payload))
  transport.on(SOFTPHONE_EVENTS.ERROR, payload => dispatch('handleTransportError', payload))
}

/**
 * Wires every cross-tab message once, guarded by the role each one belongs to.
 *
 * The guards are the point. A tab that inherits ownership keeps the mirror handlers it registered on
 * boot, and an unguarded `onState` would then let it apply a snapshot over the call state it now
 * owns - the softphone equivalent of taking dictation from itself.
 */
function _ensureChannelHandlers({ dispatch, state }) {
  _ensureChannel()

  if (areChannelHandlersRegistered) {
    return channel
  }

  areChannelHandlersRegistered = true

  channel.onState(message => {
    if (state.role !== SOFTPHONE_ROLES.MIRROR) {
      return
    }

    dispatch('applyRemoteState', message.snapshot)
  })

  channel.onClosed(() => {
    if (state.role !== SOFTPHONE_ROLES.MIRROR) {
      return
    }

    dispatch('handleOwnerClosed')
  })

  channel.onHello(() => {
    if (state.role !== SOFTPHONE_ROLES.OWNER) {
      return
    }

    _broadcastState(state)
  })

  channel.onCommand(message => {
    if (state.role !== SOFTPHONE_ROLES.OWNER) {
      return
    }

    dispatch('handleRemoteCommand', message)
  })

  return channel
}

/**
 * The single-owner lock. Created on first use rather than at import time so a tab that never has a
 * softphone user in it never asks the browser for anything.
 */
function _ensureOwnership({ dispatch }) {
  if (ownership) {
    return ownership
  }

  ownership = createSoftphoneOwnership()

  // Only ever the handover case in practice: the boot grant is returned by `request()` itself, and
  // a steal is returned by `steal()`. A handover means the previous owner's tab is gone.
  ownership.onGranted = ({ isHandover }) => {
    if (isHandover) {
      dispatch('adoptSoftphoneOwnership')
    }
  }

  ownership.onLost = () => dispatch('handleTabClaimLost')

  return ownership
}

/**
 * The offer this tab makes after inheriting a softphone that was live: "you were live for X -
 * resume?". Shaped so it can be handed straight to `goOnline`, which is why it is the campaign row
 * shape rather than the flattened `liveCampaign` it is built from.
 */
function _buildResumePrompt(liveCampaign) {
  return {
    id: liveCampaign.id,
    name: liveCampaign.name,
    category: { name: liveCampaign.categoryName || '' },
    language: { id: liveCampaign.languageId || '', name: liveCampaign.languageName || '' },
    budget: {
      minimumDurationSelected: liveCampaign.minimumDurationSelected === true,
      minimumDurationInSeconds: Number(liveCampaign.minimumDurationInSeconds) || 0
    }
  }
}

/**
 * Everything a mirror needs to render. Deliberately a hand-built object rather than the whole state:
 * `role` must never travel, or a mirror would adopt the owner's role and believe it may answer.
 */
export function buildSoftphoneSnapshot(state) {
  return {
    status: state.status,
    liveCampaign: state.liveCampaign,
    incomingCall: state.incomingCall,
    activeCall: state.activeCall,
    wrapUpEndsAt: state.wrapUpEndsAt,
    isPausedForWrapUp: state.isPausedForWrapUp,
    isEndingWrapUp: state.isEndingWrapUp,
    isMuted: state.isMuted,
    // Travels because the mirrors render the same line: a mid-call drop is the agent's own tab in
    // one window and the tab they are watching in another. `tokenExpiresAt` deliberately does NOT
    // travel - only the owner holds a token, and only the owner may re-mint one.
    isRestoringConnection: state.isRestoringConnection,
    lastError: state.lastError
  }
}

export function _broadcastState(state) {
  if (state.role !== SOFTPHONE_ROLES.OWNER || !channel) {
    return
  }

  channel.postState(buildSoftphoneSnapshot(state))
}

const state = {
  // null until `initSoftphone` has settled the election. See SOFTPHONE_ROLES.
  role: null,

  status: SOFTPHONE_STATUS.OFFLINE,

  /**
   * The campaign this tab inherited an abandoned softphone for, offered back to the agent as
   * "you were live for X - resume?".
   *
   * Set only on a handover, and only in the tab that won it. Ownership passing to a new tab must
   * NOT quietly re-register the softphone: the agent is looking at some other tab by then, and a
   * tab they are not watching would start ringing on its own. Shaped so it can be handed straight
   * to `goOnline`, and deliberately kept out of the broadcast snapshot - it belongs to the one tab
   * that has to make the offer.
   */
  resumePrompt: null,

  // The single campaign this agent is live for. Only one at a time, by product decision.
  liveCampaign: null,

  incomingCall: null,

  activeCall: null,

  wrapUpEndsAt: null,

  // True from the moment the wrap-up pause landed on the server until the wrap-up resume did. Kept
  // separately from `status` because a second call can arrive and end while it is set, and that
  // call's end must still run the wrap-up - otherwise the campaign would stay paused forever.
  isPausedForWrapUp: false,

  /**
   * True while the wrap-up's resume is in flight. The status is still `wrapUp` for the whole round
   * trip - the resume can fail, and the widget must not say Ready before the campaign is - so this
   * is the only thing that tells "wrapping up" from "about to stop wrapping up".
   *
   * Extending is refused while it is set, and both controls go dead: the campaign is already being
   * resumed, so another minute cannot be granted, and an agent shown one and then rung anyway is
   * worse off than one told the window is over.
   */
  isEndingWrapUp: false,

  isMuted: false,

  /**
   * When the Telnyx token this registration is authenticated by lapses, as the endpoint reported it
   * (`expiresAt`, decoded from the JWT's `exp`). Deliberately nullable: the endpoint returns null
   * rather than deny an agent a working token over an unreadable claim, and a widget with no expiry
   * simply has nothing to schedule - the reconnect still catches the drop when it comes.
   *
   * Owner-only, like `resumePrompt`: it describes the token THIS tab is holding, and a mirror has
   * none.
   */
  tokenExpiresAt: null,

  /**
   * The socket dropped while a call was up, so the reconnect is owed and waiting for the call to
   * end. Its own flag rather than a status, because the status must stay `ACTIVE` - the call is
   * real and the agent is on it - and the widget still has to say the connection is being restored
   * without implying the call in front of them is at risk.
   */
  isRestoringConnection: false,

  lastError: null
}

/**
 * @type {import('vuex').GetterTree<typeof state>}
 */
const getters = {
  /**
   * ENTITLEMENT SEAM. Every softphone surface is gated on this getter and nothing else. It
   * delegates to `globalSettings/isSoftphoneUser`: the global SOFTPHONE_GOES_LIVE setting AND the
   * per-user `softphoneAgent` flag. Both fail closed, so the incoming-call UI is hidden unless
   * this user holds the grant themselves.
   *
   * Do NOT repoint this at `globalSettings/canViewSoftphoneCampaigns`. That getter is wider on
   * purpose - it also admits a super-admin impersonating the account, so they can read and edit
   * the account's softphone campaigns - and registering a softphone is exactly the thing
   * impersonation must not carry: the admin's browser would take the agent's SIP identity and
   * their live calls. Visibility travels with impersonation; the SIP identity does not.
   */
  isSoftphoneAvailable(state, getters, rootState, rootGetters) {
    return rootGetters['globalSettings/isSoftphoneUser'] === true
  },

  isSoftphoneOwner(state) {
    return state.role === SOFTPHONE_ROLES.OWNER
  },

  /**
   * The agent is taking calls, but in a different tab. What the header widget says instead of the
   * call controls, alongside the offer to take the softphone over here.
   */
  isSoftphoneInAnotherTab(state) {
    return state.role === SOFTPHONE_ROLES.MIRROR && ONLINE_STATUSES.includes(state.status)
  },

  /**
   * Whether taking the softphone over in this tab is offered.
   *
   * Refused while the owner has a call on it. A takeover drops the owner's transport, and WebRTC
   * media cannot move between tabs, so it would hang up on the caller - and the agent clicking it
   * has no way to know that from here.
   */
  canTakeOverSoftphone(state) {
    return state.role === SOFTPHONE_ROLES.MIRROR && !IN_CALL_STATUSES.includes(state.status)
  },

  hasSoftphoneResumePrompt(state) {
    return Boolean(state.resumePrompt)
  },


  /**
   * What the live campaign's calls are about, which is what the widget names - the campaign's own
   * name is the buyer's bookkeeping and means nothing to the agent answering.
   */
  liveCampaignCategoryName(state) {
    return state.liveCampaign?.categoryName || ''
  },

  /**
   * The language the campaign's callers speak, as the short code the agent reads at a glance - EN,
   * ES. Empty when the campaign carries none, and the widget then simply leaves it out.
   */
  liveCampaignLanguageCode(state) {
    return String(state.liveCampaign?.languageId || '').toUpperCase()
  },

  /**
   * The same language written out - English, Spanish - which is what the widget shows the agent.
   * A code is a thing to decode; the name is read. Falls back to the code for a campaign that
   * travelled here without a name on it, and is empty only when the campaign carries no language
   * at all, which the widget then simply leaves out.
   */
  liveCampaignLanguageName(state, getters) {
    return state.liveCampaign?.languageName || getters.liveCampaignLanguageCode
  },

  /**
   * How long an answered call must run before it earns. Zero for a campaign with no minimum, which
   * the widget says in words rather than reporting "0 seconds" - that reads like a value that
   * failed to load.
   */
  liveCampaignMinimumDurationInSeconds(state) {
    if (!state.liveCampaign?.minimumDurationSelected) {
      return 0
    }

    return Math.max(0, Number(state.liveCampaign.minimumDurationInSeconds) || 0)
  },

  isSoftphoneOnline(state) {
    return ONLINE_STATUSES.includes(state.status)
  },

  isRinging(state) {
    return state.status === SOFTPHONE_STATUS.RINGING
  },

  isCallActive(state) {
    return state.status === SOFTPHONE_STATUS.ACTIVE
  },

  isWrappingUp(state) {
    return state.status === SOFTPHONE_STATUS.WRAP_UP
  },

  liveCampaignId(state) {
    return state.liveCampaign?.id || null
  },

  /**
   * How long an answered call must run before the widget shows the caller's full number; until then
   * it shows only the last four digits.
   *
   * The gate is the live campaign's MINIMUM DURATION - the point at which the call is billable, and
   * so the point at which the buyer has earned the number. A campaign without a minimum duration
   * has nothing to wait for, so `0`: the number is shown from the moment the call is answered.
   */
  callerIdRevealAfterSeconds(state) {
    if (!state.liveCampaign?.minimumDurationSelected) {
      return 0
    }

    return Math.max(0, Number(state.liveCampaign.minimumDurationInSeconds) || 0)
  },

  softphoneStatusLabel(state) {
    return getSoftphoneStatusLabel(state.status)
  }
}

/**
 * @type {import('vuex').ActionTree<typeof state>}
 */
const actions = {
  /**
   * Boots the softphone for this tab. Called once per dashboard tab, whether or not the agent ever
   * goes live - the tab has to know what the softphone is doing before it can render a header
   * badge or a campaigns list, and it has to be queued for ownership before the owner disappears.
   *
   * The lock is asked for HERE rather than in `goOnline`, because the thing that must be unique is
   * the tab holding the transport, not the call: two registered tabs would both ring.
   */
  async initSoftphone({ dispatch }) {
    const isOwner = await _ensureOwnership({ dispatch }).request()

    await dispatch(isOwner ? 'becomeOwner' : 'becomeMirror')

    return isOwner
  },

  /**
   * Adopt the owner role. Ownership itself is granted by the lock, not by this - dispatching it
   * without having won the lock is a test-only shortcut.
   */
  becomeOwner({ commit, dispatch, state }) {
    commit('SET_ROLE', SOFTPHONE_ROLES.OWNER)

    _ensureChannelHandlers({ dispatch, state })

    // Doubles as "there is an owner here now", which is what a mirror booting alongside this tab
    // needs before it can render anything but Offline.
    _broadcastState(state)
  },

  /**
   * Follow the owner. A mirror never creates a transport.
   */
  becomeMirror({ commit, dispatch, state }) {
    commit('SET_ROLE', SOFTPHONE_ROLES.MIRROR)

    _ensureChannelHandlers({ dispatch, state })

    // The owner may already be mid-call; ask where things stand rather than assume offline.
    channel.postHello()
  },

  /**
   * Take the softphone on in this tab, having just been given the lock for it.
   *
   * Shared by both ways ownership arrives at a tab that was mirroring: a HANDOVER, where the tab
   * that held it went away and the browser handed the lock on, and a TAKEOVER, where the agent
   * asked for the softphone here. Both leave the same thing behind - a campaign the other tab was
   * live for and no longer serving - and both account for it identically.
   *
   * The campaign is NOT resumed automatically. In the handover case ownership goes to whichever tab
   * the browser picked, which is not necessarily one the agent is looking at, and a tab that
   * started ringing by itself would drop calls the agent never knew had arrived. So the campaign is
   * paused - the other tab did not get to do it reliably, and a campaign left Active with no agent
   * behind it routes calls at nobody - and then offered back.
   */
  async adoptSoftphoneOwnership({ commit, dispatch, state }) {
    const inherited = state.liveCampaign

    commit('SET_ROLE', SOFTPHONE_ROLES.OWNER)

    _ensureChannelHandlers({ dispatch, state })

    // Pauses the campaign, tells the sibling mirrors and resets. They were watching the other tab,
    // not this one, so this is the only way they hear about it.
    await dispatch('handleOwnerGone')

    if (inherited) {
      commit('SET_RESUME_PROMPT', _buildResumePrompt(inherited))
    }
  },

  /**
   * Move the softphone to THIS tab, because the agent asked for it here. Deliberate and never
   * automatic: it takes the lock away from the owner, which drops that tab's transport.
   *
   * Guarded by `canTakeOverSoftphone` at the call site - a takeover mid-call would hang up on the
   * caller, since WebRTC media cannot follow the lock.
   */
  async takeOverSoftphone({ dispatch }) {
    const isOwner = await _ensureOwnership({ dispatch }).steal()

    if (!isOwner) {
      return false
    }

    await dispatch('adoptSoftphoneOwnership')

    return true
  },

  /**
   * Accepts the resume offer: go live again, in this tab, for the campaign the softphone was
   * inherited from. Both halves are needed - the server has to resume the campaign, and this tab
   * has to register - and the server call goes first, so a refused resume never leaves an agent
   * registered for a paused campaign.
   *
   * The offer is cleared only once the server has agreed. A refused resume leaves it on screen, so
   * the agent can try again rather than having the one way back to their campaign taken away.
   */
  async resumeSoftphoneFromPrompt({ commit, dispatch, state }) {
    const campaign = state.resumePrompt

    if (!campaign) {
      return
    }

    await dispatch('campaign/goLiveSoftphoneCampaign', { campaignId: campaign.id }, { root: true })

    commit('SET_RESUME_PROMPT', null)

    await dispatch('goOnline', campaign)
  },

  dismissSoftphoneResumePrompt({ commit }) {
    commit('SET_RESUME_PROMPT', null)
  },

  /**
   * Gives up the lock, so a sibling tab can take the softphone over at once instead of waiting for
   * the browser to notice this one is gone.
   */
  releaseSoftphoneOwnership() {
    if (ownership) {
      ownership.release()
    }
  },

  /**
   * Diagnostics: how many tabs hold the softphone lock, and how many are queued behind it. The
   * invariant this whole module exists for, made checkable - `heldCount` is never above 1.
   */
  describeSoftphoneOwnership({ dispatch }) {
    return _ensureOwnership({ dispatch }).describe()
  },

  applyRemoteState({ commit, dispatch, state }, snapshot) {
    if (!snapshot) {
      return
    }

    const didWrapUpPauseChange = Boolean(snapshot.isPausedForWrapUp) !== Boolean(state.isPausedForWrapUp)

    // The agent stopped being live for a campaign: the widget paused it server-side, so the row
    // this tab is showing as Active is now Paused. Covers the agent going offline from the widget
    // and the widget dropping offline by itself (a lost registration, a transport error).
    const didStopBeingLive = Boolean(state.liveCampaign) && !snapshot.liveCampaign

    commit('APPLY_REMOTE_STATE', snapshot)

    // The widget paused or resumed the campaign on the server, and it has no campaigns list to
    // refresh - this tab does. Best effort by construction (the action swallows its own failure).
    if (didWrapUpPauseChange || didStopBeingLive) {
      dispatch('campaign/refreshBuyerCampaignsIfLoaded', null, { root: true })
    }
  },

  /**
   * The owner's tab shut down CLEANLY - it has already paused the campaign, so there is nothing to
   * do but drop back to offline and make every Go Live button clickable again.
   *
   * This is also what keeps the tab that inherits the lock from offering a pointless resume: a
   * clean goodbye clears `liveCampaign` in every mirror, and `adoptSoftphoneOwnership` reads that
   * to decide whether there is anything to account for.
   */
  handleOwnerClosed({ commit, dispatch, state }) {
    const wasLive = Boolean(state.liveCampaign)

    commit('RESET_SOFTPHONE')

    // The widget paused the campaign on its way out. Only this tab has the list showing that
    // campaign as Active, so only this tab can put the badge right.
    if (wasLive) {
      dispatch('campaign/refreshBuyerCampaignsIfLoaded', null, { root: true })
    }
  },

  /**
   * The owner's tab disappeared without saying goodbye, and this tab found out by being handed the
   * lock. Reached when the tab is closed, reloaded or killed: the unload path tries to pause with a
   * keepalive request, but that is the one call that cannot be awaited or retried.
   *
   * So it pauses, because a campaign left Active with no agent behind it routes calls at nobody.
   * Pausing twice is harmless, which is what makes it safe to do here on a hunch.
   */
  async handleOwnerGone({ commit, dispatch, state }) {
    _ensureChannel()

    const wasPausedForWrapUp = state.isPausedForWrapUp

    await dispatch('pauseLiveCampaign')

    // Sibling mirrors were not handed the lock, so they only learn about it from here - and they
    // refetch their campaigns list on hearing it. Said AFTER the pause has landed, so that refetch
    // sees the row Paused; said before, it would fetch a list that still reads Active, and the
    // offline snapshot broadcast by the reset below finds nothing left to change and never triggers
    // a second one.
    channel.postClosed()

    commit('RESET_SOFTPHONE')

    // The refetch inside the pause above ran while this store still believed a wrap-up was in
    // progress, so it kept the server's wrap-up label - and the server keeps that label until the
    // campaign is resumed, which now never happens. One more refetch, made after the reset, is what
    // turns the row from "Paused (wrap-up)" into plain "Paused".
    if (wasPausedForWrapUp) {
      dispatch('campaign/refreshBuyerCampaignsIfLoaded', null, { root: true })
    }
  },

  async handleRemoteCommand({ dispatch }, { name, payload } = {}) {
    if (name === SOFTPHONE_COMMANDS.GO_ONLINE) {
      await dispatch('goOnline', payload)

      return
    }

    if (name === SOFTPHONE_COMMANDS.GO_OFFLINE) {
      // Going offline, not tearing down: the tab stays open and keeps the lock, because it is still
      // the one tab allowed to take calls and the agent may go live again in a moment.
      await dispatch('goOffline')

      return
    }

    if (name === SOFTPHONE_COMMANDS.FINISH_WRAP_UP) {
      await dispatch('finishWrapUp')

      return
    }

    if (name === SOFTPHONE_COMMANDS.EXTEND_WRAP_UP) {
      dispatch('extendWrapUp')
    }
  },

  /**
   * Go Live from wherever the agent clicked it.
   *
   * The softphone MOVES to the clicking tab. Anything else means the agent presses Go Live in the
   * tab they are working in and the call then rings in some other tab they may not even have on
   * screen. Moving it is safe here by construction: Go Live is only offered when nothing is live,
   * so the tab losing the lock has no transport and no call to drop.
   *
   * A mirror that cannot take the lock, or one whose view of the world says a call is up after all,
   * falls back to asking the owner. That fallback is not expected to be reached - the Go Live
   * button is dead while anything is live - and it exists because the alternative to a command
   * nobody acts on is a click that silently does nothing.
   */
  async requestGoOnline({ commit, dispatch, state }, campaign) {
    const payload = {
      id: campaign.id,
      name: campaign.name,
      // The category and the language are what the widget puts on screen. Narrowed here so the same
      // payload works whether it is handed to `goOnline` in this tab or structured-cloned to another.
      category: { name: campaign.category?.name || '' },
      language: { id: campaign.language?.id || '', name: campaign.language?.name || '' },
      // The minimum duration has to travel too: the widget gates the caller id on it (see
      // `callerIdRevealAfterSeconds`) and shows it beside the category. Narrowed to these fields
      // rather than passing the whole row, because this is structured-cloned on every go-live.
      budget: {
        minimumDurationSelected: campaign.budget?.minimumDurationSelected === true,
        minimumDurationInSeconds: Number(campaign.budget?.minimumDurationInSeconds) || 0
      }
    }

    const canMoveHere = state.role !== SOFTPHONE_ROLES.OWNER
      && !IN_CALL_STATUSES.includes(state.status)
      && await _ensureOwnership({ dispatch }).steal()

    if (canMoveHere) {
      commit('SET_ROLE', SOFTPHONE_ROLES.OWNER)

      _ensureChannelHandlers({ dispatch, state })

      // The mirrored view belongs to the tab that just lost the lock. Cleared rather than paused:
      // the server-side go-live behind this click has already settled which campaigns are live, and
      // a pause here would take the agent offline for the campaign they are going live for.
      commit('SET_RESUME_PROMPT', null)
      commit('RESET_SOFTPHONE')
    }

    if (state.role === SOFTPHONE_ROLES.OWNER) {
      return dispatch('goOnline', payload)
    }

    _ensureChannel()

    channel.postCommand(SOFTPHONE_COMMANDS.GO_ONLINE, payload)

    return undefined
  },

  /**
   * Go Offline from wherever the agent clicked it. Unlike Go Live this never moves the lock: the
   * tab that holds the transport is the only one that can disconnect it, and it pauses the campaign
   * on its way out, so a mirror asks rather than takes.
   */
  requestGoOffline({ dispatch, state }) {
    if (state.role === SOFTPHONE_ROLES.OWNER) {
      return dispatch('goOffline')
    }

    _ensureChannel()

    channel.postCommand(SOFTPHONE_COMMANDS.GO_OFFLINE)

    return Promise.resolve()
  },

  /**
   * End the wrap-up now, from wherever the agent clicked it. The countdown is a floor on how long
   * the agent MAY have, not on how long they must take: one who is done filing the call takes the
   * next one straight away rather than watching a clock, or going offline to beat it and having to
   * go live again.
   *
   * Ends exactly as the timer's own expiry does - `finishWrapUp` resumes the campaign and puts the
   * widget back to Ready - so there is one ending to get right, not two.
   */
  requestFinishWrapUp({ dispatch, state }) {
    if (state.status !== SOFTPHONE_STATUS.WRAP_UP) {
      return Promise.resolve()
    }

    if (state.role === SOFTPHONE_ROLES.OWNER) {
      return dispatch('finishWrapUp')
    }

    _ensureChannel()

    channel.postCommand(SOFTPHONE_COMMANDS.FINISH_WRAP_UP)

    return Promise.resolve()
  },

  /**
   * The other half: an agent who needs longer says so instead of going offline to stop the clock.
   * Asked for as many times as they like - see `extendWrapUp` for what each one buys.
   */
  requestExtendWrapUp({ dispatch, state }) {
    if (state.status !== SOFTPHONE_STATUS.WRAP_UP) {
      return Promise.resolve()
    }

    if (state.role === SOFTPHONE_ROLES.OWNER) {
      dispatch('extendWrapUp')

      return Promise.resolve()
    }

    _ensureChannel()

    channel.postCommand(SOFTPHONE_COMMANDS.EXTEND_WRAP_UP)

    return Promise.resolve()
  },

  /**
   * Go live for exactly one campaign. Must be called from a user gesture: the ringtone is
   * armed here (see `ringtone.js`).
   */
  async goOnline({ commit, dispatch, state }, campaign) {
    if (!campaign?.id) {
      throw new Error('A campaign is required to go online')
    }

    // One campaign at a time: going online anywhere else takes the agent offline first.
    if (state.liveCampaign && state.liveCampaign.id !== campaign.id) {
      await dispatch('goOffline')
    } else if (state.liveCampaign?.id === campaign.id && state.status !== SOFTPHONE_STATUS.OFFLINE && state.status !== SOFTPHONE_STATUS.ERROR) {
      return
    }

    commit('SET_LAST_ERROR', null)
    // The category, the language and the minimum duration ride along because the widget shows the
    // first two and gates the caller id on the third (see `callerIdRevealAfterSeconds`), and the tab
    // holding the softphone may never fetch campaigns itself. `name` is kept for the campaign name
    // stamped onto each call.
    commit('SET_LIVE_CAMPAIGN', {
      id: campaign.id,
      name: campaign.name,
      categoryName: campaign.category?.name || '',
      languageId: campaign.language?.id || '',
      languageName: campaign.language?.name || '',
      minimumDurationSelected: campaign.budget?.minimumDurationSelected === true,
      minimumDurationInSeconds: Number(campaign.budget?.minimumDurationInSeconds) || 0
    })
    commit('SET_STATUS', SOFTPHONE_STATUS.CONNECTING)

    await armRingtone()

    try {
      transport = await createSoftphoneTransport()

      _wireTransportEvents({ dispatch })

      exposeSoftphoneDevHook(transport)

      const token = await dispatch('mintSoftphoneToken')

      await transport.connect(token)
    } catch (error) {
      await dispatch('goOffline')

      commit('SET_STATUS', SOFTPHONE_STATUS.ERROR)
      commit('SET_LAST_ERROR', error?.response?.data?.title || error?.message || 'Could not start taking calls')

      throw error
    }
  },

  /**
   * Mints the Telnyx `login_token` this tab's registration is authenticated by, and remembers when
   * it lapses so it can be replaced before it does.
   *
   * Returns null for a transport that needs no token (the fake), which is not the same as failing:
   * the caller connects with null and the expiry is cleared, so nothing is scheduled.
   */
  async mintSoftphoneToken({ commit, rootGetters }) {
    if (!transport?.requiresToken) {
      commit('SET_TOKEN_EXPIRES_AT', null)

      return null
    }

    const response = await SoftphoneAPI.getWebRtcToken(rootGetters['auth/accountId'])

    commit('SET_TOKEN_EXPIRES_AT', response?.data?.expiresAt || null)

    return response?.data?.token || null
  },

  /**
   * Replaces the token a working registration is holding, a margin before it expires.
   *
   * The point is that the common case never becomes a reconnect: the 24h token is swapped while the
   * agent is idle instead of being repaired after the socket it authenticated dropped at 03:00. It
   * goes through the same loop as a drop, because the work is the same - new token, new client, wait
   * for the registration, resume the campaign - and one path that is exercised on every drop is
   * worth more than a second one that runs once a shift.
   */
  renewSoftphoneToken({ dispatch, state }) {
    tokenRenewalTimerId = null

    // A call is on. The token outlives the margin by design, so waiting for the call is free -
    // and rebuilding the client under a caller is not. No `isRestoringConnection` here, unlike a
    // real drop: nothing is broken, and the agent has nothing to be told.
    //
    // Deferred by REASON, and that matters: the socket under this call is working, so the call's
    // own ending will arrive and no timeout is armed - but a drop landing later in the same call
    // still has to pause the campaign, and `handleUnregistered` can only tell because this says
    // what it is waiting for rather than only that it is waiting.
    if (IN_CALL_STATUSES.includes(state.status)) {
      _deferReconnect(RECONNECT_REASONS.TOKEN_RENEWAL, dispatch)

      return Promise.resolve()
    }

    if (!RECONNECTABLE_STATUSES.includes(state.status)) {
      return Promise.resolve()
    }

    return dispatch('startReconnect', { reason: RECONNECT_REASONS.TOKEN_RENEWAL })
  },

  /**
   * Schedules the re-mint above. Called once a registration is established - a token nobody is
   * registered with is not worth replacing - and cleared by every path that ends the shift.
   */
  scheduleTokenRenewal({ dispatch, state }) {
    _clearTokenRenewalTimer()

    if (!state.tokenExpiresAt) {
      return
    }

    const expiresAt = new Date(state.tokenExpiresAt).getTime()

    if (!Number.isFinite(expiresAt)) {
      return
    }

    // Already inside the margin - a token minted with less life left than the margin, or a tab that
    // was asleep through it. Zero rather than a negative delay, which `setTimeout` would fire
    // immediately anyway but less obviously.
    const delayInMs = Math.max(0, expiresAt - Date.now() - getReconnectPolicy().tokenRenewalMarginInMs)

    tokenRenewalTimerId = setTimeout(() => dispatch('renewSoftphoneToken'), delayInMs)
  },

  /**
   * The bounded reconnect. Owns the four steps a lost registration needs and that nothing below the
   * store can do on its own: pause the campaign, re-mint the token, rebuild the client, and resume
   * the campaign once - and only once - the transport says it is registered again.
   *
   * The campaign stays paused for the whole run on purpose. Resuming on "we are trying" would route
   * calls at a browser that is not registered, which is the exact failure the pause exists to
   * prevent; so the pause is the first thing and the resume is the last.
   *
   * Success is NOT detected here. Every registration in the widget arrives as the transport's
   * `REGISTERED` event, and `handleRegistered` hands a reconnect's registration to
   * `finishReconnect` - which means the SDK quietly recovering the old socket during the first
   * backoff finishes this run exactly as our own rebuilt client would, and no token is minted at
   * all. The loop's job is only to keep trying until something registers or the attempts run out.
   */
  async startReconnect({ commit, dispatch, state }, { reason = RECONNECT_REASONS.SOCKET_CLOSED } = {}) {
    if (reconnectRun) {
      return
    }

    /**
     * Nothing to get back to. A shift with no live campaign, or one that dropped while the agent
     * was still pressing Go Live (`goOnline` reports that failure itself), goes where a lost
     * registration went before this change: offline, with the reason on screen.
     */
    if (!state.liveCampaign?.id || !RECONNECTABLE_STATUSES.includes(state.status)) {
      await dispatch('abandonReconnect')

      return
    }

    /**
     * A call is on: the reconnect is owed, not started, and `handleCallEnded` picks it up.
     *
     * The campaign is paused NOW even so, rather than when the loop eventually starts. The
     * registration is already gone, and a campaign left Active routes calls at a browser that
     * cannot take them - a second call CAN arrive while the agent is on one, which is exactly why
     * the wrap-up pauses mid-call too (see `pauseForWrapUp`). Waiting for the call to end would
     * leave that window open for the length of the call, which is the longest window there is.
     *
     * Pausing does not touch the call itself; it stops the next one being routed.
     *
     * Only reached for a DROP. The token re-mint defers on an active call as well, but it returns
     * before dispatching this (see `renewSoftphoneToken`) - the registration there is alive and
     * well, and pausing a working campaign to renew a token nobody is waiting on would cost the
     * agent calls for nothing.
     *
     * The wait is bounded, because the call's ending is reported over the socket that just went -
     * see `_deferReconnect`.
     */
    if (state.status === SOFTPHONE_STATUS.ACTIVE) {
      _deferReconnect(reason, dispatch)

      commit('SET_RESTORING_CONNECTION', true)

      // Not when the campaign is already paused for a wrap-up: that pause is not ours, and asking
      // for it again would report the agent as having walked away rather than writing up a call.
      if (!state.isPausedForWrapUp) {
        await dispatch('pauseLiveCampaign')
      }

      return
    }

    /**
     * A call that was RINGING cannot be answered any more - the answer travels over the socket that
     * just went - so it is cleared rather than left on screen. An agent reaching for Pick up on a
     * call that can no longer be picked up is worse than one told the phone is reconnecting.
     */
    if (state.incomingCall) {
      stopRinging()
      commit('SET_INCOMING_CALL', null)
    }

    /**
     * A wrap-up pause is not ours to lift. The agent earned that break and the wrap-up owns the
     * resume that ends it, so this run restores the wrap-up rather than resuming the campaign - see
     * `finishReconnect`.
     */
    const resumeOnSuccess = !state.isPausedForWrapUp

    const run = {
      attempt: 0,
      isCancelled: false,
      hasFinished: false,
      resumeOnSuccess,
      restoreStatus: resumeOnSuccess ? SOFTPHONE_STATUS.READY : SOFTPHONE_STATUS.WRAP_UP,
      timerId: null,
      resolveWait: null
    }

    reconnectRun = run

    _clearTokenRenewalTimer()
    commit('SET_RESTORING_CONNECTION', false)
    commit('SET_LAST_ERROR', null)
    commit('SET_STATUS', SOFTPHONE_STATUS.RECONNECTING)

    if (resumeOnSuccess) {
      // Best effort by construction (`pauseLiveCampaign` swallows its own failure). A pause that
      // failed is not a reason to abandon the registration - the agent would then be neither
      // reconnected nor paused.
      //
      // Asked for unconditionally, which means a run that was deferred behind a call pauses a
      // campaign the deferral already paused. Deliberate: pausing twice is harmless, and the
      // alternative is tracking whether a pause landed across an await and a call ending, for
      // nothing but one saved request.
      await dispatch('pauseLiveCampaign')
    }

    if (run.isCancelled) {
      return
    }

    const { maxAttempts, registrationTimeoutInMs } = getReconnectPolicy()

    while (!run.isCancelled && run.attempt < maxAttempts) {
      run.attempt += 1

      /**
       * The wait before the attempt, not after it. The first one is what gives the SDK's own
       * recovery room to land - it reconnects the same session without a new token, and a run that
       * rebuilt the client immediately would throw that away - and a token being replaced on
       * schedule has nothing to wait for.
       */
      const isImmediate = reason === RECONNECT_REASONS.TOKEN_RENEWAL && run.attempt === 1

      await _waitForReconnectDelay(run, isImmediate ? 0 : getReconnectDelayInMs(run.attempt))

      if (run.isCancelled) {
        return
      }

      try {
        await dispatch('rebuildTransportForReconnect')

        if (run.isCancelled) {
          return
        }

        const token = await dispatch('mintSoftphoneToken')

        if (run.isCancelled) {
          return
        }

        await transport.connect(token)
      } catch (error) {
        console.error(`Softphone reconnect attempt ${run.attempt} failed`, error)

        if (_isSessionGoneError(error)) {
          break
        }

        continue
      }

      if (run.isCancelled) {
        return
      }

      /**
       * Connected is not registered. `connect()` settles on the socket and the registration arrives
       * as an event afterwards, so the run waits for `finishReconnect` to cancel it; a socket that
       * never registers gives way to the next attempt instead of hanging the loop.
       */
      await _waitForReconnectDelay(run, registrationTimeoutInMs)
    }

    if (run.isCancelled) {
      return
    }

    reconnectRun = null

    await dispatch('abandonReconnect')
  },

  /**
   * Builds the replacement transport for one attempt, having disposed of the one that stopped
   * working.
   *
   * The old transport's listeners come off BEFORE its disconnect, as everywhere else in this module:
   * `disconnect()` emits UNREGISTERED, and an UNREGISTERED of our own making landing in
   * `handleUnregistered` mid-reconnect would read as a fresh drop.
   */
  async rebuildTransportForReconnect({ dispatch }) {
    if (transport) {
      const previous = transport

      transport = null

      previous.removeAllListeners()

      try {
        await previous.disconnect()
      } catch (error) {
        console.error('Failed to disconnect the softphone transport before reconnecting', error)
      }
    }

    // The run was cancelled while the old transport was being disposed of - the agent went offline,
    // the tab lost the lock, the page is unloading. Building the replacement now would leave a live
    // client behind a widget that believes it has none.
    if (!reconnectRun) {
      return
    }

    const nextTransport = await createSoftphoneTransport()

    /**
     * And again after the build, which is its own await: the factory loads the Telnyx chunk with a
     * dynamic `import()`, so a Go Offline, a tab handover or an unload can land inside it.
     *
     * A transport assigned after that point is one nothing will ever dispose of - the teardown that
     * cancelled the run has already looked at `transport`, found the null left above, and gone. The
     * loop returns on its own next `isCancelled` check without touching it, so the store would be
     * OFFLINE while still holding a wired client and publishing it on the dev hook.
     */
    if (!reconnectRun) {
      try {
        await nextTransport.disconnect()
      } catch (error) {
        console.error('Failed to dispose of a softphone transport built after the reconnect was cancelled', error)
      }

      return
    }

    transport = nextTransport

    _wireTransportEvents({ dispatch })

    exposeSoftphoneDevHook(transport)
  },

  /**
   * A registration landed during a reconnect - ours, or the SDK's own recovery of the old socket.
   * The end of the run either way: stop trying, resume the campaign, and put the widget back where
   * the drop found it.
   *
   * `hasFinished` is what makes the resume exactly once. Two registrations in a row are ordinary
   * (a re-register, a recovered socket that then registers again), and a second resume is not a
   * wasted request: `goLive` pauses the agent's OTHER campaigns and resumes this one, so a resume
   * that lands after the agent has moved on is a campaign going live behind their back.
   */
  async finishReconnect({ commit, dispatch, state }) {
    const run = reconnectRun

    if (!run || run.hasFinished) {
      return
    }

    run.hasFinished = true

    _cancelReconnect()

    if (!run.resumeOnSuccess) {
      commit('SET_STATUS', run.restoreStatus)

      // As on every other success path. `startReconnect` clears the renewal timer for the duration
      // of the run, so a run that ends here without rearming it would leave the rest of the shift
      // with no re-mint scheduled - and the token left to expire is the failure the renewal exists
      // to prevent.
      dispatch('scheduleTokenRenewal')

      /**
       * The wrap-up outlived the reconnect. Its timer kept running through the outage, fired while
       * the status was `RECONNECTING`, and `finishWrapUp` bailed out because the status was not
       * `WRAP_UP` - leaving a paused campaign nothing would ever resume. Closing it out here is the
       * only place that can tell.
       */
      if (!wrapUpTimerId || (state.wrapUpEndsAt || 0) <= Date.now()) {
        dispatch('finishWrapUp')
      }

      return
    }

    try {
      await dispatch('campaign/goLiveSoftphoneCampaign', { campaignId: state.liveCampaign?.id }, { root: true })
    } catch (error) {
      console.error('Reconnected, but could not resume the campaign', error)

      // Registered with a paused campaign is the one state an agent cannot detect from the widget -
      // it says Ready and the calls never come - so it is handled like a failed wrap-up resume:
      // offline, with the reason on screen and every Go Live button back.
      await dispatch('goOffline')

      commit('SET_STATUS', SOFTPHONE_STATUS.ERROR)
      commit('SET_LAST_ERROR', error?.response?.data?.title || 'The connection came back but your campaign could not be resumed. Go live again to keep taking calls.')

      return
    }

    // A call that was already being routed can land while the resume is in flight; it is being
    // handled, and the status it set is the truthful one.
    if (IN_CALL_STATUSES.includes(state.status)) {
      dispatch('scheduleTokenRenewal')

      return
    }

    if (state.status === SOFTPHONE_STATUS.RECONNECTING) {
      commit('SET_STATUS', run.restoreStatus)
    }

    dispatch('scheduleTokenRenewal')
  },

  /**
   * The end of a reconnect that never had to run: the registration came back by itself while the
   * loop was still waiting for a call to end.
   *
   * All that is owed is the campaign the drop paused - the client is registered, so rebuilding it
   * would drop the call to fix something that is already working. The wrap-up's pause is the one
   * exception, as everywhere else: it is not this run's to lift.
   */
  async finishDeferredReconnect({ commit, dispatch, state }) {
    if (deferredReconnectReason !== RECONNECT_REASONS.SOCKET_CLOSED) {
      return
    }

    // Cleared first, so a second registration - a re-register, a recovery that registers twice -
    // cannot send a second resume. `goLive` pauses the agent's OTHER campaigns, so a duplicate is
    // not a wasted request but a campaign going live behind their back.
    _clearDeferredReconnect()

    if (state.isPausedForWrapUp) {
      commit('SET_RESTORING_CONNECTION', false)
      commit('SET_LAST_ERROR', null)

      return
    }

    /**
     * The call earned a wrap-up while the connection was down, and the pause that should have
     * started it never reached the server.
     *
     * Asked again rather than resumed: the campaign is paused already - the drop paused it - so
     * resuming and re-pausing would be two requests to arrive back where it stands, and the second
     * of them could lose the race and leave the campaign live behind an agent on a break. The
     * wrap-up pause hits the same endpoint with the reason that makes the audit note say wrap-up,
     * and it is what sets the flag the hang-up reads.
     */
    if (isWrapUpPauseOwed) {
      await dispatch('pauseForWrapUp')

      commit('SET_RESTORING_CONNECTION', false)

      if (!isWrapUpPauseOwed) {
        commit('SET_LAST_ERROR', null)
      }

      return
    }

    try {
      await dispatch('campaign/goLiveSoftphoneCampaign', { campaignId: state.liveCampaign?.id }, { root: true })
    } catch (error) {
      console.error('The connection came back under a live call, but the campaign could not be resumed', error)

      /**
       * A paused campaign behind a widget that says everything is fine is the one state an agent
       * cannot see, so the work goes back where it was rather than being dropped: still on the
       * call, the deferral is restored and the call's end runs the full loop, which either resumes
       * the campaign or lands on the give-up state. Off the call already - it ended while this
       * resume was in flight - and that loop can run now.
       */
      if (IN_CALL_STATUSES.includes(state.status)) {
        _deferReconnect(RECONNECT_REASONS.SOCKET_CLOSED, dispatch)

        return
      }

      await dispatch('startReconnect', { reason: RECONNECT_REASONS.SOCKET_CLOSED })

      return
    }

    commit('SET_RESTORING_CONNECTION', false)
    commit('SET_LAST_ERROR', null)
  },

  /**
   * Where a reconnect ends when it cannot be made to work: exactly where a lost registration landed
   * before any of this existed - offline, campaign paused, and the reason on screen. Deliberately
   * no new dead end; the change can only improve on the old behaviour, never invent a worse one.
   */
  async abandonReconnect({ commit, dispatch }) {
    _cancelReconnect()

    await dispatch('goOffline')

    // After goOffline, which clears the error with the shift it belonged to.
    commit('SET_LAST_ERROR', 'The connection was lost, so your campaign has been paused. Go live again when you are ready to take calls.')
  },

  /**
   * Going offline is the OWNER's job end to end, including pausing the campaign. The tab that
   * resumed it may be gone by now - the owner's tab closing has to leave the campaign paused
   * whether or not anything else is still open.
   */
  async goOffline({ commit, dispatch, state }, { isUnloading = false } = {}) {
    clearWrapUpTimer()
    clearWrapUpEligibilityTimer()
    _cancelReconnect()
    _clearTokenRenewalTimer()
    stopRinging()

    isGoingOffline = true

    // Read before the pause, which resets it - see the second refetch at the end.
    const wasPausedForWrapUp = state.isPausedForWrapUp

    try {
      await dispatch('pauseLiveCampaign', { isUnloading })

      if (transport) {
        const currentTransport = transport

        transport = null

        try {
          await currentTransport.disconnect()
        } catch (error) {
          console.error('Failed to disconnect softphone transport', error)
        }

        currentTransport.removeAllListeners()
        removeSoftphoneDevHook()
      }

      // The ownership lock is NOT released here. Going offline leaves this tab open and still the
      // one allowed to take calls; the lock is held until the tab itself goes away. Releasing it
      // would hand the softphone to whichever tab happens to be queued next, for no reason.
      commit('RESET_SOFTPHONE')

      /**
       * The last error goes with the shift it belonged to. Every failure path sets its reason AFTER
       * calling this action - a lost registration, a transport error, a wrap-up resume that failed -
       * so clearing it here cannot swallow one; what it clears is the reason for a shift that has
       * already ended.
       *
       * It matters because an error is one of the two things that keep the header's toggle alive
       * while the status says Offline (see `canOpenPanel`). Left set, a shift the agent ended by
       * hand would go on offering them a panel with a stale explanation in it.
       */
      commit('SET_LAST_ERROR', null)

      /**
       * Going offline mid wrap-up: the row has to stop reading "Paused (wrap-up)" and read plain
       * "Paused", because the wrap-up is over - the agent ended it.
       *
       * It takes a SECOND refetch. The one inside the pause above ran while this store still
       * believed a wrap-up was in progress, so it kept the server's wrap-up label - and the server
       * keeps that label until the campaign is resumed, which now never happens (go-offline only
       * touches campaigns that are still Active, and this one is already paused). Made after the
       * reset, so `isCampaignPausedForWrapUp` finds no wrap-up to honour. Same reason and the same
       * remedy as `handleOwnerGone`.
       *
       * Not on the unload path: that tab is going away, and its pause is a keepalive request with
       * nothing left to render the answer.
       */
      if (wasPausedForWrapUp && !isUnloading) {
        dispatch('campaign/refreshBuyerCampaignsIfLoaded', null, { root: true })
      }
    } finally {
      isGoingOffline = false
    }
  },

  /**
   * Tells the server the agent is offline, which pauses EVERY softphone campaign ringing them - not
   * just the one this window went live for - so calls stop being routed to a destination with nobody
   * behind it. Best effort: a failure here must not stop the transport from disconnecting, because
   * leaving the agent connected-but-offline is worse than a campaign that stays active.
   *
   * Skipped when this window never went live: it then has nothing to take offline, and a widget that
   * merely opened and closed must not pause campaigns an agent is live for elsewhere.
   */
  async pauseLiveCampaign({ dispatch, state, rootGetters }, { isUnloading = false } = {}) {
    if (!state.liveCampaign?.id) {
      return
    }

    const accountId = rootGetters['auth/accountId']

    // An XHR started in `beforeunload` is cancelled with the page, so the unload path uses fetch
    // with keepalive instead. It skips the axios auth-refresh interceptor - acceptable, because the
    // alternative is not pausing at all, and an expired token means the agent's session was already
    // over. Every other path keeps the interceptor.
    if (isUnloading) {
      goOfflineWithKeepalive(accountId, rootGetters['auth/accessToken'])

      return
    }

    try {
      // The root campaign action rather than SoftphoneAPI directly: importing the api module here
      // creates a cycle (api -> httpClient -> router -> store -> this module) that leaves the store
      // half-built at import time.
      await dispatch('campaign/goOfflineSoftphone', null, { root: true })
    } catch (error) {
      console.error('Failed to take the softphone agent offline', error)
    }
  },

  /**
   * Full teardown for a tab that is going away - closed, reloaded or logged out. Losing the tab
   * counts as going offline, but only in the OWNER: a mirror renders the owner's campaign without
   * holding it, and a mirror that paused on its way out would take the agent offline in the tab
   * that is still on the call. A mirror just leaves the queue for the lock and closes its channel.
   *
   * `isUnloading` is the path that cannot wait for anything - `pagehide`, and a logout whose
   * session may already be dead. It pauses with a keepalive request, says goodbye, releases the lock
   * and only then drops the transport, without awaiting it: the Telnyx disconnect settles on a
   * socket event, and a page that is unloading is gone before that arrives. Awaiting it left the
   * goodbye and the release unsent, so every close of a live tab looked to its siblings like a
   * crash - a second pause and a "take calls from here?" the agent had not earned.
   *
   * The ordering of pause, goodbye, release carries meaning. A `closed` message means this tab
   * paused and there is nothing left to do; a tab that vanishes WITHOUT one leaves the mirrors
   * holding its last state, and whichever of them inherits the lock pauses on its behalf (see
   * `handleOwnerGone`). Announcing before pausing would make the two indistinguishable.
   *
   * Re-entrant: a second call while one is in flight joins it. A logout can arrive mid-teardown
   * when the pause request itself fails to authenticate.
   */
  teardownSoftphone({ commit, dispatch, state }, { isUnloading = false } = {}) {
    if (teardownPromise) {
      return teardownPromise
    }

    const run = async () => {
      // Captured up front: a logout resets the whole store while this is in flight.
      const wasOwner = state.role === SOFTPHONE_ROLES.OWNER

      // Before the role check, and unconditionally: the tab is going away, and a reconnect that
      // outlived it would mint a token for a shift with no browser behind it. `goOffline` on the
      // deliberate path cancels it too; the unload path never reaches that.
      _cancelReconnect()
      _clearTokenRenewalTimer()

      if (!wasOwner) {
        _disposeTabResources()
        commit('SET_ROLE', null)

        return
      }

      if (!isUnloading) {
        await dispatch('goOffline')

        if (channel) {
          channel.postClosed()
        }

        _disposeTabResources()
        commit('SET_ROLE', null)

        return
      }

      clearWrapUpTimer()
      clearWrapUpEligibilityTimer()
      stopRinging()

      // Synchronous in effect: the keepalive request is handed to the browser before this returns.
      await dispatch('pauseLiveCampaign', { isUnloading: true })

      if (channel) {
        channel.postClosed()
      }

      commit('RESET_SOFTPHONE')

      _disposeTabResources()
      _dropTransportWithoutWaiting()

      // Neither role now. Cleared last so the reset above still went out as the owner's word, and so
      // a second teardown - a logout's safety net after the deliberate one - finds nothing to do.
      commit('SET_ROLE', null)
    }

    teardownPromise = run().finally(() => {
      teardownPromise = null
    })

    return teardownPromise
  },

  async answerCall({ state }) {
    if (!state.incomingCall || !transport) {
      return
    }

    await transport.answer(state.incomingCall.id)
  },

  async declineCall({ state }) {
    if (!state.incomingCall || !transport) {
      return
    }

    await transport.reject(state.incomingCall.id)
  },

  async hangUp({ state }) {
    if (!state.activeCall || !transport) {
      return
    }

    await transport.hangup(state.activeCall.id)
  },

  async setMuted({ commit }, isMuted) {
    if (transport) {
      await transport.setMuted(isMuted)
    }

    commit('SET_MUTED', isMuted)
  },

  /**
   * Every registration in the widget arrives here, which is why the reconnect does not detect its
   * own success: whether the client was rebuilt by an attempt or the SDK quietly recovered the old
   * socket during the first backoff, this is where it lands.
   */
  handleRegistered({ commit, dispatch, state }) {
    if (reconnectRun) {
      return dispatch('finishReconnect')
    }

    /**
     * A registration arriving on a reconnect that is only OWED, not running. The common shape of
     * this is the one the loop never gets to see: the socket dropped mid-call, so the run was
     * deferred behind the caller, and then the SDK's own recovery got the session back on its own.
     *
     * Nothing was left to reconnect - but the campaign the drop paused was still paused and the
     * widget still said the connection was being restored, for the rest of the call and a whole
     * needless rebuild afterwards.
     */
    if (deferredReconnectReason === RECONNECT_REASONS.SOCKET_CLOSED) {
      return dispatch('finishDeferredReconnect')
    }

    if (state.status === SOFTPHONE_STATUS.CONNECTING) {
      commit('SET_STATUS', SOFTPHONE_STATUS.READY)

      dispatch('scheduleTokenRenewal')
    }

    return undefined
  },

  /**
   * The registration is gone, so Telnyx has nowhere to send a call - but the campaign is still
   * live, and Ringba would keep routing calls at a destination that cannot answer them.
   *
   * Which of the two things happened decides everything:
   *
   * - REQUESTED. This event belongs to a teardown of ours, and that teardown sets the status
   *   itself. Nothing to do.
   * - A DROP. A laptop lid, a Wi-Fi hop, a VPN reconnect, a proxy idle timeout, a token that
   *   outlived the shift. None of those is worth an agent's shift, so the widget gets the
   *   registration back instead of ending it - and only when that fails does it land where it used
   *   to land immediately (offline, campaign paused, the reason on screen).
   */
  async handleUnregistered({ commit, dispatch, state }, { requested = false } = {}) {
    if (state.status === SOFTPHONE_STATUS.OFFLINE) {
      return
    }

    // `isGoingOffline` as well as the flag on the event: the transports report a teardown of their
    // own accurately, but an older transport, or one that dropped as the teardown began, does not
    // have to be trusted to for the store to behave.
    if (isGoingOffline || requested) {
      commit('SET_STATUS', SOFTPHONE_STATUS.OFFLINE)

      return
    }

    // A drop while a reconnect is already in flight, or a drop already owed, belongs to that run.
    // Starting a second loop would double the attempts and race two clients at the same SIP
    // identity.
    //
    // A TOKEN_RENEWAL deferral is NOT that. It was owed behind a call on a socket that was working,
    // and this is that socket going: falling through upgrades it to a drop, which is what pauses
    // the campaign and tells the agent. Swallowing it here would leave the campaign Active at
    // Ringba behind an unregistered browser for the rest of the call - the longest window there is,
    // and the one the pause exists to close.
    if (reconnectRun || deferredReconnectReason === RECONNECT_REASONS.SOCKET_CLOSED) {
      return
    }

    await dispatch('startReconnect', { reason: RECONNECT_REASONS.SOCKET_CLOSED })
  },

  /**
   * A call can still land during wrap-up: one that was already being routed when the campaign
   * paused. It is taken like any other, and `isPausedForWrapUp` stays set so its end runs the
   * wrap-up again and the campaign is eventually resumed.
   */
  handleIncomingCall({ commit, state }, { callId, campaignName, callerNumber } = {}) {
    clearWrapUpTimer()

    commit('SET_WRAP_UP_ENDS_AT', null)
    commit('SET_INCOMING_CALL', {
      id: callId,
      // v1 shows the campaign and nothing else; the transport has no campaign of its own.
      campaignName: campaignName || state.liveCampaign?.name || '',
      // Carried but deliberately NOT shown while ringing - the widget only reveals it on an
      // answered call. See `callerId` in SoftphoneWidget.
      callerNumber: callerNumber || null,
      receivedAt: Date.now()
    })
    commit('SET_STATUS', SOFTPHONE_STATUS.RINGING)

    startRinging()
  },

  /**
   * Answered ONCE per call, however many times the event arrives.
   *
   * The SDK re-emits `active` for a call it re-synced - which is what a socket recovered under a
   * live call does, because `keepConnectionAliveOnSocketClose` keeps the call itself up - and the
   * call was never re-answered. Treating that as a new answer restarted the duration the agent is
   * watching AND the wrap-up eligibility clock, so a nine minute call that met a Wi-Fi blip looked
   * like a fresh one and lost the break it had already earned.
   */
  handleCallAnswered({ commit, dispatch, state }, { callId, callerNumber } = {}) {
    stopRinging()

    const id = callId || state.incomingCall?.id
    const answered = state.activeCall?.id === id ? state.activeCall : null

    commit('SET_ACTIVE_CALL', {
      id,
      campaignName: answered?.campaignName || state.incomingCall?.campaignName || state.liveCampaign?.name || '',
      // The ringing call already carried it; the answered event repeats it, so prefer whichever
      // arrived - a call picked up in another window has no local incomingCall to read from.
      callerNumber: callerNumber || answered?.callerNumber || state.incomingCall?.callerNumber || null,
      answeredAt: answered?.answeredAt || Date.now()
    })
    commit('SET_INCOMING_CALL', null)
    commit('SET_STATUS', SOFTPHONE_STATUS.ACTIVE)

    // Everything below belongs to the answer itself. A re-sync must not reset the agent's mute -
    // the recovery keeps the media, so what they chose still holds - and must not re-arm the
    // eligibility timer, which has been running for this call all along.
    if (answered) {
      return
    }

    commit('SET_MUTED', false)

    // A call that is still up this long earns a wrap-up; the campaign is paused now, mid-call, so
    // nothing new is routed at an agent who is about to need a break. See WRAP_UP_* above.
    clearWrapUpEligibilityTimer()
    wrapUpEligibilityTimerId = setTimeout(() => dispatch('pauseForWrapUp'), WRAP_UP_ELIGIBLE_CALL_DURATION_IN_MS)
  },

  /**
   * Pauses the live campaign server-side because the current call has run long enough to earn a
   * wrap-up. Best effort: a failure leaves the call untouched and the agent told, and the call then
   * ends straight into Ready as a short one would.
   */
  async pauseForWrapUp({ commit, dispatch, state }) {
    clearWrapUpEligibilityTimer()

    if (state.status !== SOFTPHONE_STATUS.ACTIVE || state.isPausedForWrapUp || !state.liveCampaign?.id) {
      return
    }

    try {
      await dispatch('campaign/pauseSoftphoneForWrapUp', null, { root: true })
    } catch (error) {
      console.error('Failed to pause the campaign for wrap-up', error)

      commit('SET_LAST_ERROR', 'Could not pause the campaign for wrap-up - the next call may arrive as soon as this one ends')

      // Remembered rather than given up on, because the likeliest reason this failed is the one
      // the widget is already dealing with: the agent's connection is down. The break stays owed
      // and `finishDeferredReconnect` asks again once the connection is back - while the call is
      // still up, which is the only time the answer is still useful.
      isWrapUpPauseOwed = IN_CALL_STATUSES.includes(state.status)

      return
    }

    // The request was in flight for a while; the world may have moved on. An agent who went offline
    // meanwhile wanted the campaign paused anyway, and the reset already cleared this flag - setting
    // it now would leave a stale "paused for wrap-up" on an offline widget.
    if (!ONLINE_STATUSES.includes(state.status)) {
      return
    }

    commit('SET_PAUSED_FOR_WRAP_UP', true)

    /**
     * The list is refetched HERE, after the flag, and that ordering is the whole point.
     *
     * "Paused (wrap-up)" is resolved as each row arrives, against this store's own state - see
     * `isCampaignPausedForWrapUp`. A refetch made before the flag lands strips the label instead of
     * showing it, which is exactly what a refetch inside `pauseSoftphoneForWrapUp` used to do.
     *
     * It never showed while the softphone lived in a window of its own: that window had no list, so
     * its refetch was a no-op and the dashboard tab picked the label up from the broadcast below.
     * The owning tab now has the list in front of the agent and has to refresh it itself - the
     * broadcast only ever reaches the OTHER tabs.
     */
    dispatch('campaign/refreshBuyerCampaignsIfLoaded', null, { root: true })

    // The call ended while the pause was in flight and handleCallEnded, seeing no pause yet, went
    // back to Ready. The campaign IS paused now, so the wrap-up has to start here or nothing would
    // ever resume it.
    if (state.status === SOFTPHONE_STATUS.READY) {
      dispatch('beginWrapUp')
    }
  },

  /**
   * Ended ONCE per call, however many times the ending arrives.
   *
   * Telnyx reports it twice - `hangup` and then `destroy`, both of which the transport maps to
   * ENDED - and running the teardown again restarts the wrap-up window, because `beginWrapUp` sets
   * a fresh deadline from now. Observed at 0.4s between the two, so the agent quietly loses
   * whatever `destroy` trails `hangup` by off their break.
   *
   * The id is taken from the state when the event carries none, so the deferral's own timeout
   * (which settles a call whose ending can no longer be reported) claims the same call and the
   * real ENDED that may still arrive afterwards is recognised as the repeat it is.
   */
  handleCallEnded({ commit, dispatch, state }, { callId } = {}) {
    const endingCallId = callId || state.activeCall?.id || state.incomingCall?.id || null

    if (endingCallId && endingCallId === endedCallId) {
      return
    }

    endedCallId = endingCallId

    stopRinging()
    clearWrapUpTimer()
    clearWrapUpEligibilityTimer()

    commit('SET_INCOMING_CALL', null)
    commit('SET_ACTIVE_CALL', null)
    commit('SET_MUTED', false)

    if (state.isPausedForWrapUp) {
      dispatch('beginWrapUp')
    } else if (IN_CALL_STATUSES.includes(state.status) || state.status === SOFTPHONE_STATUS.WRAP_UP) {
      // A short call, a decline, a missed call: the campaign was never paused, so the agent is
      // simply eligible for the next call. Anything else (offline, error, another tab) is left as
      // it is.
      commit('SET_STATUS', SOFTPHONE_STATUS.READY)
    }

    /**
     * The socket dropped while this call was up, or the token came due, and the reconnect waited
     * for the call to finish - see `handleUnregistered` and `renewSoftphoneToken`. Started here,
     * after the call's own ending has been settled, so the state the run restores on success is
     * the one the call left behind (Ready, or the wrap-up it just earned).
     */
    if (deferredReconnectReason) {
      // The reason is carried through rather than assumed to be a drop: a token being replaced on
      // schedule has nothing to wait for and starts its first attempt at once, while a drop takes
      // the first backoff - which is also the window the SDK's own recovery lands in.
      const reason = deferredReconnectReason

      _clearDeferredReconnect()

      commit('SET_RESTORING_CONNECTION', false)

      dispatch('startReconnect', { reason })
    }
  },

  beginWrapUp({ commit, dispatch }) {
    clearWrapUpTimer()

    commit('SET_WRAP_UP_ENDS_AT', Date.now() + WRAP_UP_DURATION_IN_MS)
    commit('SET_STATUS', SOFTPHONE_STATUS.WRAP_UP)

    wrapUpTimerId = setTimeout(() => dispatch('finishWrapUp'), WRAP_UP_DURATION_IN_MS)
  },

  /**
   * Another WRAP_UP_DURATION_IN_MS, as many times as the agent asks for it. Added to the END of the
   * window rather than restarting it, so an agent who asks with 40s still on the clock is given 100s
   * and never loses time by asking for more.
   *
   * The campaign is already paused - nothing goes to the server here, only the deadline this store
   * resumes on moves, and the broadcast carries the new one to every other tab's countdown.
   */
  extendWrapUp({ commit, dispatch, state }) {
    // `isEndingWrapUp` rather than the status: the status is still `wrapUp` while the resume that
    // ends the window is in flight, and a minute granted there is a minute the agent does not get -
    // the resume lands a moment later and takes them back to Ready regardless.
    if (state.status !== SOFTPHONE_STATUS.WRAP_UP || state.isEndingWrapUp) {
      return
    }

    clearWrapUpTimer()

    // A deadline already in the past means the expiry is mid-flight or the tab was asleep through
    // it; extending from now rather than from that deadline is what makes the extra minute a minute.
    const wrapUpEndsAt = Math.max(state.wrapUpEndsAt || 0, Date.now()) + WRAP_UP_DURATION_IN_MS

    commit('SET_WRAP_UP_ENDS_AT', wrapUpEndsAt)

    wrapUpTimerId = setTimeout(() => dispatch('finishWrapUp'), wrapUpEndsAt - Date.now())
  },

  /**
   * End of the wrap-up window: resume the campaign the agent was live on. A resume that fails leaves
   * a paused campaign behind a Ready-looking widget, which is the one state an agent cannot detect
   * from the widget alone, so it is handled like a failed goOnline - the widget goes offline with the
   * error showing and every Go Live button comes back so the agent can go live again.
   */
  async finishWrapUp({ commit, dispatch, state }) {
    clearWrapUpTimer()

    if (state.status !== SOFTPHONE_STATUS.WRAP_UP) {
      return
    }

    /**
     * Single flight, and the status cannot say so: it stays `wrapUp` for the whole resume round
     * trip, so the expiry, a Ready click and a mirror's command can all pass the check above while
     * one resume is already out.
     *
     * A second resume is not a wasted request, it is a broken campaign. The first ends the wrap-up
     * and leaves the widget Ready; the second then returns to a store that is no longer wrapping up
     * and reads that as the agent having gone offline underneath it, which pauses the campaign -
     * behind a widget that says Ready and an agent who is waiting for calls that never come.
     */
    if (state.isEndingWrapUp) {
      return
    }

    commit('SET_WRAP_UP_ENDS_AT', null)

    if (!state.isPausedForWrapUp) {
      commit('SET_STATUS', SOFTPHONE_STATUS.READY)

      return
    }

    // The window is over from here on, whatever the resume does: the status has to stay `wrapUp`
    // until the campaign is really live again, so this is what stops Extend granting a minute that
    // the resume landing a moment later would take straight back.
    commit('SET_ENDING_WRAP_UP', true)

    try {
      await dispatch('campaign/resumeSoftphoneAfterWrapUp', { campaignId: state.liveCampaign?.id }, { root: true })
    } catch (error) {
      console.error('Failed to resume the campaign after wrap-up', error)

      /**
       * The resume failed because the CONNECTION did, and a reconnect is already putting that
       * right. Going offline here would cancel that run - `goOffline` calls `_cancelReconnect` -
       * and cost the agent a shift that a blip was seconds away from giving back.
       *
       * This is the shape a laptop lid opening makes: the wrap-up timer expired while the machine
       * slept, so `finishWrapUp` and the dropped socket are both noticed on wake, and the resume
       * goes out over a connection that is not up yet.
       *
       * Left owed instead. `isPausedForWrapUp` is still set and the window's timer is already
       * cleared, so the run restores `WRAP_UP` and its own close-out re-runs this resume; a run
       * that cannot get the registration back still ends on `abandonReconnect`, which is this
       * same offline-with-the-reason state by another road.
       */
      if (reconnectRun || deferredReconnectReason) {
        return
      }

      await dispatch('goOffline')

      commit('SET_STATUS', SOFTPHONE_STATUS.ERROR)
      commit('SET_LAST_ERROR', error?.response?.data?.title || 'Could not resume the campaign after wrap-up. Go live again to keep taking calls.')

      return
    } finally {
      commit('SET_ENDING_WRAP_UP', false)
    }

    if (state.status === SOFTPHONE_STATUS.WRAP_UP) {
      commit('SET_PAUSED_FOR_WRAP_UP', false)
      commit('SET_STATUS', SOFTPHONE_STATUS.READY)

      return
    }

    // A call landed while the resume was in flight: the campaign is live again and the call is being
    // handled; only the flag needs clearing.
    if (IN_CALL_STATUSES.includes(state.status)) {
      commit('SET_PAUSED_FOR_WRAP_UP', false)

      return
    }

    /**
     * A drop landed while the resume was in flight. Not the agent going offline: the reconnect has
     * already paused the campaign itself and it restores the wrap-up on success - which re-runs
     * this resume - so the pause below would be a spurious account-wide go-offline on top of a run
     * that is handling this.
     */
    if (state.status === SOFTPHONE_STATUS.RECONNECTING) {
      return
    }

    // The agent went offline while the resume was in flight. Their go-offline and this go-live raced
    // on the server, and if the resume landed last the campaign is Active with nobody behind it.
    // Pausing again is harmless if the pause won; it is the only fix if it lost.
    try {
      await dispatch('campaign/goOfflineSoftphone', null, { root: true })
    } catch (pauseError) {
      console.error('Failed to re-pause the campaign after a wrap-up resume raced going offline', pauseError)
    }
  },

  /**
   * A transport error can mean the registration is gone (a dead websocket, a failed re-register),
   * and the same reasoning as handleUnregistered applies: a live campaign would go on having calls
   * routed at a browser that cannot take them. The campaign is paused first, then the error is
   * restored on top of the reset so the agent is left looking at what happened.
   */
  async handleTransportError({ commit, dispatch, state }, { message } = {}) {
    stopRinging()

    /**
     * A reconnect is running or owed, and a failing attempt is expected to complain on its way
     * down. The loop is the thing that decides whether the shift survives - it has attempts left,
     * and it ends with the agent offline and the reason on screen when it does not - so an error
     * event here must not end the shift underneath it.
     */
    if (reconnectRun || deferredReconnectReason === RECONNECT_REASONS.SOCKET_CLOSED) {
      console.error('Softphone transport error while reconnecting', message)

      return
    }

    if (isGoingOffline) {
      commit('SET_LAST_ERROR', message || 'Connection error')
      commit('SET_STATUS', SOFTPHONE_STATUS.ERROR)

      return
    }

    /**
     * The drop arrives HERE first, not at `handleUnregistered`, whenever the socket dies abruptly -
     * a laptop lid, a Wi-Fi hop, a VPN reconnect. The Telnyx SDK emits `telnyx.error` from the
     * websocket's own `onerror` and only then `telnyx.socket.close`, and the browser fires `error`
     * before `close` for every unclean drop. Ending the shift here would therefore end it for the
     * exact failures this reconnect exists for - and worse, silently: `goOffline` calls the
     * transport's `disconnect()`, so the close that follows carries `requested`, and
     * `handleUnregistered` reads a drop of the network's making as a teardown of ours.
     *
     * So an error in a state a registration can be recovered from starts the loop, and the loop
     * owns what happens next. `startReconnect` sends every other state - no live campaign, still
     * connecting - straight to `abandonReconnect`, which is the offline-with-the-reason-on-screen
     * this used to do immediately.
     */
    if (state.liveCampaign?.id && RECONNECTABLE_STATUSES.includes(state.status)) {
      console.error('Softphone transport error, reconnecting', message)

      await dispatch('startReconnect', { reason: RECONNECT_REASONS.SOCKET_CLOSED })

      return
    }

    await dispatch('goOffline')

    commit('SET_STATUS', SOFTPHONE_STATUS.ERROR)
    commit('SET_LAST_ERROR', message || 'Connection error')
  },

  /**
   * Another tab took the softphone over. Drop the transport without re-claiming, so the agent has
   * exactly one tab taking calls.
   *
   * Deliberately does NOT pause the campaign: the new owner is authoritative, and it pauses and
   * offers the campaign back itself (see `takeOverSoftphone`), so pausing here would fight it.
   *
   * This tab becomes an ordinary mirror rather than a dead one. The OTHER_TAB status is what it
   * shows until the new owner answers the hello below, which usually arrives immediately - it is
   * the visible state only when the takeover left nobody broadcasting.
   */
  async handleTabClaimLost({ commit, dispatch, state }) {
    clearWrapUpTimer()
    clearWrapUpEligibilityTimer()
    // The new owner registers for this agent; a reconnect here would put a second client on the
    // same SIP identity and Telnyx would fork an inbound call to both.
    _cancelReconnect()
    _clearTokenRenewalTimer()
    stopRinging()

    if (transport) {
      const currentTransport = transport

      transport = null

      // Listeners go BEFORE the disconnect, not after. Both transports emit UNREGISTERED from
      // their own `disconnect()`, and `handleUnregistered` takes the agent offline - which would
      // pause the campaign the new owner may have just gone live for, and leave this window
      // showing a lost-connection error for a handover that worked.
      currentTransport.removeAllListeners()

      try {
        await currentTransport.disconnect()
      } catch (error) {
        console.error('Failed to disconnect softphone transport', error)
      }

      removeSoftphoneDevHook()
    }

    // The role goes first, so the reset below is not broadcast as this tab's last word on a
    // softphone it no longer owns.
    commit('SET_ROLE', SOFTPHONE_ROLES.MIRROR)
    commit('RESET_SOFTPHONE')
    commit('SET_RESUME_PROMPT', null)
    commit('SET_STATUS', SOFTPHONE_STATUS.OTHER_TAB)

    _ensureChannelHandlers({ dispatch, state })

    channel.postHello()
  }
}

/**
 * @type {import('vuex').MutationTree<typeof state>}
 */
const mutations = {
  SET_ROLE(state, role) {
    state.role = role
  },

  /**
   * Applies a snapshot broadcast by the owner. Deliberately does NOT touch `role`: a mirror that
   * adopted the owner's role would start believing it may answer calls.
   */
  APPLY_REMOTE_STATE(state, snapshot) {
    state.status = snapshot.status
    state.liveCampaign = snapshot.liveCampaign
    state.incomingCall = snapshot.incomingCall
    state.activeCall = snapshot.activeCall
    state.wrapUpEndsAt = snapshot.wrapUpEndsAt
    state.isPausedForWrapUp = Boolean(snapshot.isPausedForWrapUp)
    state.isEndingWrapUp = Boolean(snapshot.isEndingWrapUp)
    state.isMuted = snapshot.isMuted
    state.isRestoringConnection = Boolean(snapshot.isRestoringConnection)
    state.lastError = snapshot.lastError
  },

  SET_STATUS(state, status) {
    state.status = status
  },

  SET_LIVE_CAMPAIGN(state, campaign) {
    state.liveCampaign = campaign
  },

  SET_INCOMING_CALL(state, call) {
    state.incomingCall = call
  },

  SET_ACTIVE_CALL(state, call) {
    state.activeCall = call
  },

  SET_WRAP_UP_ENDS_AT(state, wrapUpEndsAt) {
    state.wrapUpEndsAt = wrapUpEndsAt
  },

  SET_PAUSED_FOR_WRAP_UP(state, isPausedForWrapUp) {
    state.isPausedForWrapUp = isPausedForWrapUp
  },

  SET_ENDING_WRAP_UP(state, isEndingWrapUp) {
    state.isEndingWrapUp = isEndingWrapUp
  },

  SET_MUTED(state, isMuted) {
    state.isMuted = isMuted
  },

  SET_TOKEN_EXPIRES_AT(state, tokenExpiresAt) {
    state.tokenExpiresAt = tokenExpiresAt
  },

  SET_RESTORING_CONNECTION(state, isRestoringConnection) {
    state.isRestoringConnection = Boolean(isRestoringConnection)
  },

  SET_LAST_ERROR(state, lastError) {
    state.lastError = lastError
  },

  SET_RESUME_PROMPT(state, resumePrompt) {
    state.resumePrompt = resumePrompt
  },

  // `role` is deliberately untouched: resetting the call state does not stop this tab owning the
  // softphone. Neither is `resumePrompt`, which outlives the reset by construction - it is set from
  // the state this very mutation clears.
  RESET_SOFTPHONE(state) {
    state.status = SOFTPHONE_STATUS.OFFLINE
    state.liveCampaign = null
    state.incomingCall = null
    state.activeCall = null
    state.wrapUpEndsAt = null
    state.isPausedForWrapUp = false
    state.isEndingWrapUp = false
    state.isMuted = false
    state.isRestoringConnection = false
    // The token went with the registration it authenticated. Left set, the next go-live would
    // schedule a re-mint against the expiry of a token nothing is holding any more.
    state.tokenExpiresAt = null
  }
}

// Test seam: lets a spec inject a FakeSoftphoneTransport without going through `goOnline`.
export const __testing = {
  setTransport(nextTransport) {
    transport = nextTransport
  },

  getTransport() {
    return transport
  },

  setOwnership(nextOwnership) {
    ownership = nextOwnership
  },

  getOwnership() {
    return ownership
  },

  hasPendingWrapUpTimer() {
    return wrapUpTimerId !== null
  },

  hasPendingWrapUpEligibilityTimer() {
    return wrapUpEligibilityTimerId !== null
  },

  isWrapUpPauseOwed() {
    return isWrapUpPauseOwed
  },

  hasPendingTokenRenewalTimer() {
    return tokenRenewalTimerId !== null
  },

  isReconnecting() {
    return reconnectRun !== null
  },

  getReconnectAttempt() {
    return reconnectRun ? reconnectRun.attempt : 0
  },

  isReconnectDeferred() {
    return deferredReconnectReason !== null
  },

  getDeferredReconnectReason() {
    return deferredReconnectReason
  },

  hasPendingDeferredReconnectTimer() {
    return deferredReconnectTimerId !== null
  },

  setChannel(nextChannel) {
    channel = nextChannel
  },

  getChannel() {
    return channel
  },

  reset() {
    clearWrapUpTimer()
    clearWrapUpEligibilityTimer()
    _cancelReconnect()
    _clearTokenRenewalTimer()
    transport = null
    ownership = null
    channel = null
    areChannelHandlersRegistered = false
    teardownPromise = null
    isGoingOffline = false
    endedCallId = null
  }
}

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations
}
